import { useEffect, useMemo, useRef, useState } from "react";

import styles from "./list.module.css";

import { DateTimeDisplay, Icons, LoggedTag } from "@kalyzee/escaet-ui";
import { MeetingOutputDTO, TimeEventTargetModels } from "@kalyzee/kast-web-sdk/http";
import { Loading, Touchable, useTriggerable } from "@kalyzee/kast-web-sdk/web-components";
import DatePicker from "react-datepicker";
import { PageMasterMode, usePageContextRef } from "../../../../components/navigation/PageContext";
import { useSettings } from "../../../../hooks/settings";
import { SessionMode } from "../../../../store/session/slices";

import { useTimeEventsOfTheDay } from "@kalyzee/kast-web-sdk";
import { isToday } from "date-fns";
import SamiiEscaetLogo from "../../../../assets/logo/samii-escaet.svg";
import { MediaSelector } from "../../../../components/media/MediaSelector";
import { TimeEventsAgenda } from "../../../../components/utils/TimeEventsAgenda";
import { sdkClient } from "../../../../helpers/sdk";
import { toastError } from "../../../../helpers/toast";
import { useUser } from "../../../../store/session/hooks";
import { useStudentContext } from "../context";
import { LoadingView } from "../../../../components/utils/LoadingView";
import { ErrorView } from "../../../../components/utils/ErrorView";

const AUTO_EVENT_SELECTION_FAVORED_AFTER_A_DELAY = 10 * 60 * 1000; // 10 minutes

export const MeetingListStudentPage = () => {
  const pageContextRef = usePageContextRef<SessionMode.MASTER>();
  const studentCtx = useStudentContext();
  const [date, setDate] = useState<Date>(new Date()); // new Date()
  const user = useUser();

  const [selectedEventId, setSelectedEventId] = useState<string>();
  const selectedEventAtRef = useRef<Date>();
  const logoContainerRef = useRef<HTMLImageElement>(null);
  const [settings] = useSettings();
  const meetingId = pageContextRef.current?.searchParams?.get("meeting");

  const [mode, setMode] = useState<"meeting" | "list">(meetingId ? "meeting" : "list");
  const { timeEvents, currentTimeEvents, error, retry } = useTimeEventsOfTheDay(sdkClient, date, user ? { type: "user", id: user._id }: undefined, false, {
    meeting: mode === "meeting" ? meetingId ?? undefined : undefined,
    targets: [TimeEventTargetModels.Meeting],
    populate: { room: settings.dev, actor: { identity: true } },
  });
  const currentTimeEvent = useMemo(() => currentTimeEvents.length ? currentTimeEvents[0] : undefined, [currentTimeEvents]);

  useTriggerable(logoContainerRef, { clickCount: 3 }, () => pageContextRef.current?.openSettingsPopup()); // press 'k' and click 3 times with mouse(left button) on logo

  // ---------------------------------------------------------------------- //
  // ---------------------------   UTILS ----------------------------- //
  // ---------------------------------------------------------------------- //

  const onJoinEvent = (eventId: string) => {
    const selectedEvent = timeEvents?.find((event) => event.id === eventId);
    const selectedMeeting = selectedEvent ? (selectedEvent.target as MeetingOutputDTO) : undefined;
    if (selectedMeeting) {
      pageContextRef.current?.goTo(PageMasterMode.MEETING_DASHBOARD, { id: selectedMeeting._id });
    }
  };

  // ---------------------------------------------------------------------- //
  // ---------------------------   EFFECT ----------------------------- //
  // ---------------------------------------------------------------------- //

  useEffect(() => {
    if (!isToday(date)) return;
    // // check date every minute
    const interval = setInterval(() => {
      if (!isToday(date.getDate())) {
        setDate(new Date());
      }
    }, 60000);
    return () => clearInterval(interval);
  }, [date]);

  useEffect(() => {
    const now = new Date();
    if (!selectedEventId && currentTimeEvent) setSelectedEventId(currentTimeEvent._id);
    else if (selectedEventAtRef.current) {
      const updateAfter = selectedEventAtRef.current.getTime() + AUTO_EVENT_SELECTION_FAVORED_AFTER_A_DELAY - now.getTime();
      if (updateAfter > 0) {
        const timeout = setTimeout(() => {
          if (currentTimeEvent) setSelectedEventId(currentTimeEvent._id);
        }, updateAfter);
        return () => {
          clearTimeout(timeout);
        };
      } else {
        if (currentTimeEvent) setSelectedEventId(currentTimeEvent._id);
      }
    }
  }, [currentTimeEvent, selectedEventId]);

  useEffect(() => {
    if (!selectedEventId) return;
    selectedEventAtRef.current = new Date();
  }, [selectedEventId]);


  // ---------------------------------------------------------------------- //
  // ---------------------------   RENDER ----------------------------- //
  // ---------------------------------------------------------------------- //

  if (error) return <ErrorView retry={() => { retry(); }} />;
  if (!studentCtx) return <LoadingView />;

  const renderEvents = () => {
    if (mode === "meeting") {
      // render only one meeting
      const timeEvent = timeEvents?.length ? timeEvents[0] : undefined;
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: "0.5em",
            minWidth: "40%",
          }}
        >
          <TimeEventsAgenda
            timeEvents={timeEvent}
            selectedEventId={selectedEventId}
            onSelectEvent={setSelectedEventId}
            onJoinEvent={onJoinEvent}
            kind="student"
          />
          <Touchable
            onPress={() => {
              setMode("list");
              pageContextRef.current?.goTo(PageMasterMode.LIST, undefined);
            }}
            style={{ width: "100%", textAlign: "center", color: "white", fontSize: "1rem" }}
          >
            Revenir à la liste
          </Touchable>
        </div>
      );
    }
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          gap: "0.5em",
        }}
      >
        {settings.dev ? (
          <DatePicker
            showIcon
            selected={date}
            date={date ?? undefined}
            onChange={(d) => {
              if (!d) return;
              setDate(d);
            }}
          />
        ) : null}
        <TimeEventsAgenda
          timeEvents={timeEvents?.filter((event) => (event.target ? true : false)) ?? []}
          selectedEventId={selectedEventId}
          onSelectEvent={setSelectedEventId}
          onJoinEvent={onJoinEvent}
          kind="student"
        />
      </div>
    );
  };

  return (
    <div className={styles.backgroundHome}>
      <div className={styles.headerHome}>
        <div className={styles.headerElements}>
          <img ref={logoContainerRef} src={SamiiEscaetLogo} alt="logo" />
        </div>
        <div className={styles.headerElements}>
          <LoggedTag userProfile={user?.identity?.avatar ?? ""} name={user?.identity?.firstname ?? "Utilisateur"} role="Apprenant" />
          <Touchable onClick={() => pageContextRef.current?.goTo(PageMasterMode.LOGOUT)}>
            <img src={Icons.ApprenantExit} alt="logo" />
          </Touchable>
        </div>
      </div>
      <div className={styles.mainHomeLogged}>
        <div className={styles.mainHomeLoggedContent}>
          <MediaSelector
            style={{
              width: 500,
              height: 300,
            }}
            audioStreamEnabled={studentCtx.audioStreamEnabled}
            videoStreamEnabled={studentCtx.videoStreamEnabled}
            defaultInputAudioDeviceInfo={studentCtx.inputAudioDeviceInfo}
            defaultInputVideoDeviceInfo={studentCtx.inputVideoDeviceInfo}
            defaultOutputAudioDeviceInfo={studentCtx.outputAudioDeviceInfo}
            onInputAudioDeviceInfo={(device) => {
              if (device) studentCtx.setInputAudioDeviceInfo?.(device);
            }}
            onInputVideoDeviceInfo={(device) => {
              if (device) studentCtx.setInputVideoDeviceInfo?.(device);
            }}
            onOutputAudioDeviceInfo={(device) => {
              if (device) studentCtx.setOutputAudioDeviceInfo?.(device);
            }}
            onAudioStreamEnabled={(enabled) => studentCtx.setAudioStreamEnabled?.(enabled)}
            onVideoStreamEnabled={(enabled) => studentCtx.setVideoStreamEnabled?.(enabled)}
            onMediaCaptureError={(type, error) => {
              if (type === "audio") {
                toastError("Veuillez autoriser le navigateur à accéder au microphone");
                studentCtx.setAudioStreamEnabled?.(false);
              }
              if (type === "video") {
                toastError("Veuillez autoriser le navigateur à accéder à la caméra");
                studentCtx.setVideoStreamEnabled?.(false);
              }
            }}
          />
          {renderEvents()}
        </div>
      </div>
      <div className={styles.footerHome}>
        <DateTimeDisplay cx="textLeft" />
      </div>
    </div>
  );
};
